/**
 * PurchaseOrderPickerModal Component
 *
 * Searches purchase orders and hands the picked one to the caller — the entry point of
 * the purchase order automation dry-runs.
 */
import { useQuery } from '@apollo/client/react';
import { useState } from 'react';
import { Icon, Input, Loader, Modal, useDebounce } from '@insio/ui-toolkit';
import { formatMoney } from '@insio/decimal';
import { PurchaseOrdersPickerDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

const PICKER_PAGE_SIZE = 8;

export interface PurchaseOrderPickerModalProps {
  isOpen: boolean;
  /** Closes the modal without picking an order. */
  onClose: () => void;
  /** Receives the picked order; the modal closes right after. */
  onSelect: (purchaseOrderId: string) => void;
  title: string;
  description?: string;
  testId?: string;
}

export function PurchaseOrderPickerModal({
  isOpen,
  onClose,
  onSelect,
  title,
  description,
  testId
}: PurchaseOrderPickerModalProps) {
  const t = useFeatureTranslation('systemSettings.automation.purchaseOrderPicker');

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, loading: isSearching } = useQuery(PurchaseOrdersPickerDocument, {
    variables: {
      filter: { search: debouncedSearch || undefined },
      first: PICKER_PAGE_SIZE
    },
    fetchPolicy: 'cache-and-network',
    skip: !isOpen
  });

  const orders = data?.purchaseOrdersConnection?.edges?.map(edge => edge.node) ?? [];

  const handleSelect = (purchaseOrderId: string) => {
    onSelect(purchaseOrderId);
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={onClose} size='sm' testId={testId}>
      <Modal.Header label={title} description={description} onClose={onClose} variant='white' />
      <Modal.Content className='h-auto! min-h-0 px-lg'>
        <div className='relative'>
          <Icon
            name='magnifying-glass'
            className='absolute left-3 top-1/2 -translate-y-1/2 w-4! h-4! text-foreground-muted pointer-events-none'
            aria-hidden='true'
          />
          <Input
            placeholder={t('searchPlaceholder')}
            value={search}
            onChange={event => setSearch(event.target.value)}
            inputMode='search'
            className='pl-xl'
            testId='purchase-order-picker-search'
          />
        </div>

        {isSearching && orders.length === 0 ? (
          <Loader.Section />
        ) : orders.length === 0 ? (
          <p className='text-sm text-foreground-muted text-center py-md'>{t('noResults')}</p>
        ) : (
          <div>
            {orders.map(order => (
              <button
                key={order.id}
                type='button'
                onClick={() => handleSelect(order.id)}
                data-testid={`purchase-order-picker-select-${order.id}`}
                className='w-full flex items-center gap-sm py-sm px-sm text-left rounded-md border-b border-border-base last:border-0 cursor-pointer transition-colors hover:bg-background-muted'
              >
                <Icon
                  name='bag-shopping'
                  className='h-5 w-4 text-foreground-muted shrink-0'
                  aria-hidden='true'
                />
                <span className='flex-1 min-w-0'>
                  <span className='block text-sm font-semibold text-foreground-base truncate'>
                    {order.number}
                    {order.title ? ` · ${order.title}` : ''}
                  </span>
                  {order.supplierName && (
                    <span className='block text-xs text-foreground-muted truncate'>
                      {order.supplierName}
                    </span>
                  )}
                </span>
                {order.totalAmountNoVat != null && order.currencyCode && (
                  <span className='shrink-0 text-xs font-medium text-foreground-base whitespace-nowrap'>
                    {formatMoney(order.totalAmountNoVat, { currencyCode: order.currencyCode })}
                  </span>
                )}
                <Icon
                  name='chevron-down'
                  rotate={270}
                  className='w-4 h-4 text-foreground-disabled shrink-0'
                  aria-hidden='true'
                />
              </button>
            ))}
          </div>
        )}
      </Modal.Content>
    </Modal>
  );
}
